import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import GoogleMapReact from 'google-map-react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';

import CircularProgress from '@material-ui/core/CircularProgress';
import {
  Grid,
  Paper,
  Collapse,
  IconButton,
  Button,
  Link,
  useMediaQuery,
  TextField,
} from '@material-ui/core';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import PinDropIcon from '@mui/icons-material/PinDrop';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DrawIcon from '@mui/icons-material/Draw';
import NotListedLocationIcon from '@mui/icons-material/NotListedLocation';

import {
  mapOptions,
  BACKGROUND_COLOR,
  COLOR_PRIMARY_DARK,
  COLOR_PRIMARY_LIGHT,
  COLOR_ACCENT_1,
  COLOR_PRIMARY,
  COLOR_RED,
  MARKET_SEARCH_HISTORY,
  RATE_LIMIT_ERROR_MESSAGE,
} from 'src/utils/const';
import { TailoredRegionPointer, MarketPointer } from 'src/App/Dashboards/Market/Components/Pointer';
import { EditTailoredRegionNameModal, ErrorDialog } from '..';
import { OnboardingModalPropTypes, SearchProp } from 'src/interfaces/iproptypes';
import { callApi } from 'src/core/callApi';
import { MarketInMapViewport, TailoredRegionMarkers } from 'src/interfaces/idashboard';
import { LinearProgress, Box, Typography, Modal } from '@mui/material';
import postOnSlack from 'src/core/postOnSlack';
import { useDispatch, useSelector } from 'react-redux';
import { User } from 'src/interfaces/iuser';
import { GOOGLE_MAPS_API_KEY } from 'src/utils/keys';
import {
  formatMarketName,
  getPolygonPaths,
  getTailoredRegionByTimezone,
  setPolygon,
  abbreviateNumber,
} from 'src/utils/functions';
import { COLOR_PRIMARY_2, MAP_FILL_COLOR } from 'src/utils/colors';
import { useUpdateCustomMarketName } from 'src/hooks/useCustomHooks';
import { updateMarketInList, getMarketsInMapViewport, setTailoredRegionCreationRateLimited } from 'src/store/Dashboard/actions';
import { Bounds } from 'src/interfaces/idashboard';
import RateLimitAlert from '../../Components/Alerts/RateLimitAlert';

const parseWKTtoPaths = (wktString: string | undefined): google.maps.LatLngLiteral[] => {
  if (!wktString || !wktString.toUpperCase().startsWith('POLYGON((') || !wktString.endsWith('))')) {
    // console.warn('Invalid or empty WKT string provided:', wktString);
    return [];
  }
  try {
    const coordsString = wktString.substring(
      wktString.indexOf('((') + 2,
      wktString.lastIndexOf('))')
    );
    if (!coordsString) {
      // console.warn('Empty coordinates in WKT string:', wktString);
      return [];
    }
    const pairs = coordsString.split(',');
    // A valid polygon requires at least 3 distinct vertices, forming 3 pairs for the path, plus the closing pair.
    // However, Google Maps API can render polygons with 3+ vertices without explicit closing.
    if (pairs.length < 3) {
      // console.warn('Not enough coordinate pairs for a polygon in WKT string:', wktString);
      return [];
    }

    const paths: google.maps.LatLngLiteral[] = [];
    for (const pair of pairs) {
      const parts = pair.trim().split(' ');
      if (parts.length !== 2) {
        // console.warn('Invalid coordinate pair format in WKT string:', pair);
        continue;
      }
      const lng = parseFloat(parts[0]);
      const lat = parseFloat(parts[1]);
      if (isNaN(lat) || isNaN(lng)) {
        // console.warn('Invalid number in coordinate pair in WKT string:', pair);
        continue;
      }
      paths.push({ lat, lng });
    }

    if (paths.length < 3) {
      // After filtering invalid pairs
      // console.warn('Not enough valid coordinate pairs for a polygon after parsing WKT string:', wktString);
      return [];
    }

    return paths;
  } catch (error) {
    // console.error("Error parsing WKT string:", wktString, error);
    return [];
  }
};

const RIPPLE_SIZE = 100;
const RIPPLE_COLOR = COLOR_ACCENT_1;

const useStyles = makeStyles((theme: Theme) => ({
  paperModal: {
    backgroundColor: BACKGROUND_COLOR,
    padding: theme.spacing(2),
    animation: 'fade-scale-in 0.3s ease forwards',
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    zIndex: 2000,
    overflowX: 'hidden',
    overflowY: 'clip',
  },
  tailoredRegionMapContainer: {
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
  },
  sideButtonWrapper: {
    position: 'absolute',
    top: 20,
    right: 20,
    display: 'flex',
    justifyContent: 'space-around',
    flexDirection: 'column',
    zIndex: 100,
    gap: '10px',
  },
  tailoredRegionMapAlert: {
    height: 'max(50px, 10vh)',
    width: '100%',
    overflowY: 'auto',
    margin: 'auto',
    marginBottom: 15,
    boxSizing: 'border-box',
  },
  iconButton: {
    background: '#fff',
    boxShadow: '0px -2px 7px -1px rgba(0, 0, 0, 0.45)',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: '#222',
      boxShadow: '0px 0px 12px 1px rgba(0, 0, 0, 0.5)',
      color: '#fff',
    },
  },
  tailoredRegionMapButton: {
    marginTop: 0,
    marginBottom: 0,
  },
  tailoredRegionMapBottomButtonContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: 20,
    width: '100%',
    justifyContent: 'center',
    background: '#fff',
    position: 'absolute',
    left: 0,
    bottom: 0,
    zIndex: 100,
    borderRadius: '50px 50px 0 0',
    boxShadow: '0px -2px 7px -1px rgba(0, 0, 0, 0.25)',
    fontWeight: 'bold',
    height: '40vh',
    transition: 'height 0.3s ease',
  },
  button: {
    background: COLOR_PRIMARY_LIGHT,
    borderRadius: 100,
    border: 0,
    color: 'white !important',
    padding: '10px 30px',
    boxShadow: '0 0 5px 1px rgba(255, 255, 255, .3)',
    '&:hover': {
      background: COLOR_PRIMARY_DARK,
    },
    fontSize: 16,
    width: '70%',
    [theme.breakpoints.down(359)]: {
      fontSize: 16,
    },
    transition: 'transform 0.3s ease',
  },
  ripple: {
    position: 'absolute',
    top: 0,
    left: 0,
    marginTop: -RIPPLE_SIZE / 2,
    marginLeft: -RIPPLE_SIZE / 2,
    width: RIPPLE_SIZE,
    height: RIPPLE_SIZE,
    animation: `$rippleAnimation 1000ms ${theme.transitions.easing.easeOut}`,
    animationIterationCount: 'infinite',
    opacity: 0,
    borderRadius: '50%',
    boxShadow: `0 0 6px 3px ${RIPPLE_COLOR}`,
  },
  '@keyframes rippleAnimation': {
    '0%': {
      transform: 'scale(0.1, 0.1)',
      opacity: 0,
    },
    '1%': {
      opacity: 1,
    },
    '50% ': {
      opacity: 1,
    },
    '100%': {
      transform: 'scale(1.2, 1.2)',
      opacity: 0,
    },
  },
}));

const togglePolygonDrawing = (maps, drawingManager, isDrawing, setIsDrawing) => {
  if (drawingManager) {
    const mode = isDrawing ? null : maps.drawing.OverlayType.POLYGON;
    drawingManager.setDrawingMode(mode);
    setIsDrawing(true);
  }
};

const initiateMapFromDrawing = (polygon, setPolygon, setIsLoading) => {
  polygon && polygon.setMap(null);
  setPolygon(null);
  setIsLoading(false);
};

const removeDrawingTool = drawingManager => {
  drawingManager.setDrawingMode(null);
  drawingManager.setOptions({
    drawingControl: false,
  });
};

const getTailorRegionAllowed = (tailoredListings: number, user: User) => {
  if (tailoredListings < 10) {
    return false;
  }
  if (tailoredListings > 30000 && !user.email.endsWith('@airbtics.com')) {
    return false;
  }
  return true;
};

const handleTailorRegionCreation = (
  polygon,
  map,
  maps,
  drawingManager,
  setTailorRegionName,
  polygonArray,
  setTailoredListings,
  setIsLoading,
  setApiCallStatus,
  initiateMapFromDrawing: (polygon: any, setPolygon: any, setIsLoading) => void,
  setDisableButton,
  tailorRegionName,
  user,
  checkOrUpdateTailoredRegionName,
  setEditableName,
  setPolygon,
  setTailoredRegionId,
  dispatch
) => {
  removeDrawingTool(drawingManager);
  dispatch(setTailoredRegionCreationRateLimited(false));

  let polygonString = '';
  polygonArray.forEach((polygon, i) => {
    polygonString += `(${polygon.toString().replace(',', ' ')}),`;
  });
  polygonString = polygonString.slice(0, -1);

  let listingUrl = `https://8jmjhnmtei.execute-api.us-east-2.amazonaws.com/default/getTotalSupplyForRegion?polygon=${polygonString}`;

  axios.get(listingUrl).then(res => {
    setTailoredListings(res.data.message);
    let isTailorRegionAllowed = getTailorRegionAllowed(res.data.message, user);
    if (!isTailorRegionAllowed) {
      setApiCallStatus('notAllowed');
      initiateMapFromDrawing(polygon, setPolygon, setIsLoading);
      return;
    }

    const createTailorRegionUrl =
      'https://oz287au41h.execute-api.us-east-2.amazonaws.com/default/createTailorRegion';

    const createTailorRegionConfig = {
      queryParams: {
        countryCode: res.data.countryCode,
        polygon: polygonString,
        apiType: 'v2'
      },
    };

    setTimeout(async () => {
      const params = new URLSearchParams(window.location.search);
      const hasName = params.get('lat') && params.get('lng') && params.get('name');

      try {
        try {
          let createTailorRegionResponse = await callApi(
            createTailorRegionUrl,
            createTailorRegionConfig,
            true,
            false,
            '',
            'GET',
            true,
            undefined,
            true,
            true
          );

          if (createTailorRegionResponse && createTailorRegionResponse.status === 429) {
            dispatch(setTailoredRegionCreationRateLimited(true));
            setIsLoading(false);            
            setDisableButton(true);
            // setApiCallStatus('');
            initiateMapFromDrawing(polygon, setPolygon, setIsLoading);            
            return;
          }

          let createTailoreRegionResponseObj = createTailorRegionResponse.message;
          let regionName = createTailoreRegionResponseObj['district'];
          let marketID = createTailoreRegionResponseObj['market_id'];
          if (marketID) {
            setTailoredRegionId(marketID);
          }
          if (!hasName) {
            setTailorRegionName(regionName);
            setEditableName(regionName);
          } else {
            await checkOrUpdateTailoredRegionName('create');
          }
          setApiCallStatus('success');
          setIsLoading(false);
          setDisableButton(false);
        } catch (e) {
          console.log('Error while calling /createTailorRegion is', e);
          setApiCallStatus('failed');
          initiateMapFromDrawing(polygon, setPolygon, setIsLoading);
        }
      } catch (e) {
        console.log('Unhandled error point 50 ', e);
        setApiCallStatus('failed');
        initiateMapFromDrawing(polygon, setPolygon, setIsLoading);
      }
    }, 1500);
  });
};

const OnboardingModalMobile = ({
  getTokenThunk,
  setIsTailoredRegionOpen,
  tailoredRegionMarkers,
  getTailoredRegionMarkers,
  title,
  center,
  zoom,
  showMarketPolygon,
  setSubmarketPolygonPaths,
  confirmationButtonText,
  handleConfirmation,
  hideListingsCount,
  alertText,
  loading,
  AlertTextComponent,
  predefinedboundaryWKT,
  boundaryInfo,
}: OnboardingModalPropTypes) => {
  const classes = useStyles();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));

  const handleMapChange = useCallback(
    ({ bounds }: { bounds: GoogleMapReact.Bounds }) => {
      getTailoredRegionMarkers(bounds, showMarketPolygon ? true : false);
      dispatch(
        getMarketsInMapViewport({
          northEast: {
            lat: bounds.ne.lat,
            lng: bounds.ne.lng,
          },
          southWest: {
            lat: bounds.sw.lat,
            lng: bounds.sw.lng,
          },
        } as Bounds)
      );
    },
    [getTailoredRegionMarkers]
  );

  const user = useSelector(state => (state as any).dashboard.user);
  const search = useSelector(state => (state as any).dashboard.search);
  const marketsInMapViewport = useSelector(state => (state as any).dashboard.marketsInMapViewport);

  const [tailorRegionStartInfo, setTailorRegionStartInfo] = useState(true);
  const [open, setOpen] = useState(true);
  const isLoggedIn = Boolean(getTokenThunk());
  const history = useHistory();
  const [triggerRectangle, setTriggerRectangle] = useState(false);
  const [tailoredListings, setTailoredListings] = useState(0);
  const [tailorRegionName, setTailorRegionName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [apiCallStatus, setApiCallStatus] = useState('');
  const [disableButton, setDisableButton] = useState(true);
  const [centerLat, setCenterLat] = useState(39.210878338011376);
  const [centerLng, setCenterLng] = useState(-101.51684523289367);
  const [homeLat, setHomeLat] = useState(39.210878338011376);
  const [homeLng, setHomeLng] = useState(-101.51684523289367);
  const [locationAllowed, setLocationAllowed] = useState(false);
  const [maps, setMaps] = useState<any>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingManagerCopy, setDrawingManagerCopy] = useState(null as any);
  const [polygonCopy, setPolygonCopy] = useState(null as any);
  const [downBarSize, setDownBarSize] = useState<string>('90px');
  const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);
  const [polygonAssistant, setPolygonAssistant] = useState<{
    isOpen: boolean;
    top: number;
    left: number;
  }>({ isOpen: false, top: 0, left: 0 });

  const [wrongLatLng, setWrongLatLng] = useState<boolean>(false);
  const [polygonOutOfBounds, setPolygonOutOfBounds] = useState(false);
  const [tailoredRegionId, setTailoredRegionId] = useState<string | number | null>(null);
  const [loadingCustomMarket, setLoadingCustomMarket] = useState<boolean>(false);

  const {
    editableName,
    setEditableName,
    nameLoading,
    nameUpdateStatus,
    handleNameCheck,
    checkOrUpdateTailoredRegionName,
  } = useUpdateCustomMarketName(tailorRegionName, setTailorRegionName);

  const savedMarketsState = useSelector((state: any) => state.dashboard.savedMarkets);
  const { data: selectedList } = savedMarketsState;

  const params = new URLSearchParams(window.location.search);
  const dispatch = useDispatch();

  const tailoredRegionCreationRateLimited = useSelector((state: any) => state.dashboard.tailoredRegionCreationRateLimited);

  const defaultCoord = getTailoredRegionByTimezone();

  const getDefaultCenterLocation = () => {
    return defaultCoord
      ? defaultCoord
      : locationAllowed
        ? { lat: centerLat, lng: centerLng }
        : params.has('lat') &&
          params.has('lng') &&
          !isNaN(Number(params.get('lat'))) &&
          !isNaN(Number(params.get('lng')))
          ? { lat: Number(params.get('lat')), lng: Number(params.get('lng')) }
          : { lat: 39.210878338011376, lng: -101.51684523289367 };
  };

  useEffect(() => {
    if (matches) {
      setIsInfoModalOpen(true);
    }
  }, [matches]);

  useEffect(() => {
    const lat = params.get('lat');
    const lng = params.get('lng');
    const name = params.get('name');

    if (lat && lng) {
      if (isNaN(Number(lat)) || isNaN(Number(lng))) {
        setWrongLatLng(true);
      } else {
        setCenterLat(Number(lat));
        setCenterLng(Number(lng));
        setHomeLat(Number(lat));
        setHomeLng(Number(lng));
        setLocationAllowed(true);

        if (name) {
          const name_from_url =
            params
              .get('name')
              ?.toString()
              .trim() !== ''
              ? (params.get('name') + '').trim()
              : '';
          console.log('Name from url: ', name_from_url);

          if (name_from_url !== '') {
            setTailorRegionName(name_from_url);
          }
        }
      }
    }
    // console.log(`%cLat & Lng: (${lat} , ${lng})`, 'color: cyan; font-size: 30px');
  }, []);

  const loadPolygonDrawButtonAssistant = next => {
    setTimeout(() => {
      const polygonDrawButton: any = document.querySelector("button[aria-label='Draw a shape']");
      if (polygonDrawButton) {
        const rect = polygonDrawButton.getBoundingClientRect();
        // console.log(`%cTop: ${rect.top}, left: ${rect.left}`, 'color: cyan; font-size: 30px');
        setPolygonAssistant(prev => ({ top: rect.top, left: rect.left, isOpen: true }));
        next();

        const removeAssistant = () => {
          setPolygonAssistant(prev => ({ ...prev, isOpen: false }));
          polygonDrawButton.removeEventListener('click', removeAssistant);
        };

        polygonDrawButton.addEventListener('click', removeAssistant);
      }
    }, 1000);
  };

  const callbackAfterMapIsLoaded = (map, maps, polygonRaw: string, multiPolygonRaw: string) => {
    const tilesLoadListener = maps.event.addListener(map, 'tilesloaded', () =>
      loadPolygonDrawButtonAssistant(() => maps.event.removeListener(tilesLoadListener))
    );

    if (showMarketPolygon) {
      const paths = getPolygonPaths(polygonRaw, multiPolygonRaw);
      const polygon = setPolygon(map, maps, paths, COLOR_PRIMARY_DARK, MAP_FILL_COLOR);
    }
  };

  const takeAction = (
    setSubmarketPolygonPaths,
    polygonArray,
    polygon,
    map,
    maps,
    drawingManager,
    setTailoredRegionId
  ) => {
    if (setSubmarketPolygonPaths) {
      setPolygonOutOfBounds(false);
      setIsLoading(false);
      removeDrawingTool(drawingManager);
      setSubmarketPolygonPaths(polygonArray);
      setDisableButton(false);
    } else {
      handleTailorRegionCreation(
        polygon,
        map,
        maps,
        drawingManager,
        setTailorRegionName,
        polygonArray,
        setTailoredListings,
        setIsLoading,
        setApiCallStatus,
        initiateMapFromDrawing,
        setDisableButton,
        tailorRegionName,
        user,
        checkOrUpdateTailoredRegionName,
        setEditableName,
        setPolygonCopy,
        setTailoredRegionId,
        dispatch
      );
    }
  };

  const tailoredRegionApiIsLoaded = (map, maps, polygonRaw: string, multiPolygonRaw: string) => {
    setMaps(maps);

    const drawingOptions = {
      drawingMode: null,
      drawingControl: false,
      polygonOptions: {
        editable: true,
        draggable: false,
      },
    };

    const drawingManager = new maps.drawing.DrawingManager(drawingOptions);
    setDrawingManagerCopy(drawingManager);
    drawingManager.setMap(map);

    const setupPolygonListeners = targetPolygon => {
      maps.event.clearInstanceListeners(targetPolygon.getPath());

      maps.event.addListener(targetPolygon.getPath(), 'set_at', () => {
        let currentPolygonArray: number[][] = [];
        targetPolygon
          .getPath()
          .getArray()
          .forEach(path => {
            currentPolygonArray.push([path.lat(), path.lng()]);
          });
        setIsLoading(true);
        setTriggerRectangle(true);
        takeAction(
          setSubmarketPolygonPaths,
          currentPolygonArray,
          targetPolygon,
          map,
          maps,
          drawingManager,
          setTailoredRegionId
        );
      });

      maps.event.addListener(targetPolygon.getPath(), 'insert_at', () => {
        let currentPolygonArray: number[][] = [];
        targetPolygon
          .getPath()
          .getArray()
          .forEach(path => {
            currentPolygonArray.push([path.lat(), path.lng()]);
          });
        setIsLoading(true);
        setTriggerRectangle(true);
        takeAction(
          setSubmarketPolygonPaths,
          currentPolygonArray,
          targetPolygon,
          map,
          maps,
          drawingManager,
          setTailoredRegionId
        );
      });
    };

    if (predefinedboundaryWKT) {
      const paths = parseWKTtoPaths(predefinedboundaryWKT);
      if (paths.length > 0) {
        const predefinedPolygon = new maps.Polygon({
          paths: paths,
          editable: true,
          draggable: false,
          strokeColor: COLOR_PRIMARY_DARK,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: MAP_FILL_COLOR,
          fillOpacity: 0.35,
          map: map,
        });

        setPolygonCopy(predefinedPolygon);
        setIsDrawing(false);

        const bounds = new maps.LatLngBounds();
        predefinedPolygon
          .getPath()
          .getArray()
          .forEach(point => {
            bounds.extend(point);
          });
        map.fitBounds(bounds);

        let polygonArray: number[][] = [];
        predefinedPolygon
          .getPath()
          .getArray()
          .forEach(path => {
            polygonArray.push([path.lat(), path.lng()]);
          });

        setIsLoading(true);
        setTriggerRectangle(true);

        takeAction(
          setSubmarketPolygonPaths,
          polygonArray,
          predefinedPolygon,
          map,
          maps,
          drawingManager,
          setTailoredRegionId
        );
        setDownBarSize('min(250px, 40%)');

        setupPolygonListeners(predefinedPolygon);
      }
    }

    maps.event.addListener(drawingManager, 'polygoncomplete', newlyDrawnPolygon => {
      setIsDrawing(false);
      setPolygonCopy(newlyDrawnPolygon);

      let polygonArray: number[][] = [];
      let polygonPaths = newlyDrawnPolygon.getPath().getArray();
      let isOutOfBounds = false;

      if (showMarketPolygon) {
        const allowedBoundary = new maps.Polygon({
          paths: getPolygonPaths(polygonRaw, multiPolygonRaw),
        });
        polygonPaths.forEach(path => {
          if (!maps.geometry.poly.containsLocation(path, allowedBoundary)) {
            isOutOfBounds = true;
          }
        });
      }

      if (isOutOfBounds) {
        setPolygonOutOfBounds(true);
        newlyDrawnPolygon.setMap(null);
        setPolygonCopy(null);
        setDownBarSize('min(250px, 40%)');
        return;
      }

      setPolygonOutOfBounds(false);
      polygonPaths.forEach(path => {
        polygonArray.push([path.lat(), path.lng()]);
      });

      setIsLoading(true);
      setTriggerRectangle(true);

      takeAction(
        setSubmarketPolygonPaths,
        polygonArray,
        newlyDrawnPolygon,
        map,
        maps,
        drawingManager,
        setTailoredRegionId
      );
      setDownBarSize('min(250px, 40%)');
      setupPolygonListeners(newlyDrawnPolygon);
    });
  };

  //hide tawk bot
  const _window: any = window;
  const tawkWidgetToggle = show => {
    // Ensure the Tawk object has initalized
    if (_window.$_Tawk && _window.$_Tawk.init) {
      show ? showWidget() : hideWidget();
    } else {
      // If the Tawk object didn't initilize, use a differnt method of loading
      if (_window.Tawk_API) {
        _window.Tawk_API.onLoad = function () {
          show ? showWidget() : hideWidget();
        };
      }
    }
  };

  const showWidget = () => {
    _window.Tawk_API.showWidget();
  };

  const hideWidget = () => {
    _window.Tawk_API.hideWidget();
  };

  useEffect(() => {
    tawkWidgetToggle(false);

    return () => {
      tawkWidgetToggle(true);
    };
  }, []);

  const locationPin = useMemo(() => {
    return (
      <div>
        <div className={classes.ripple}></div>
        <PinDropIcon
          style={{
            fontSize: 40,
            position: 'absolute',
            top: -20,
            left: -20,
          }}
        />
      </div>
    );
  }, [centerLat, centerLng]);

  let markers = useMemo(() => {
    if (tailoredRegionMarkers && tailoredRegionMarkers.length > 0) {
      return tailoredRegionMarkers.map((item: TailoredRegionMarkers, index: number) => (
        <TailoredRegionPointer key={index} lat={item.latitude} lng={item.longitude} data={item} />
      ));
    }
    return;
  }, [tailoredRegionMarkers]);

  const marketInMapViewportMarkers = useMemo(() => {
    const maxMarketsToShow = matches ? 3 : 20;
    if (!marketsInMapViewport.data) {
      return <></>;
    }
    return marketsInMapViewport.data
      .slice(0, maxMarketsToShow)
      .map((market: MarketInMapViewport, index: number) => {
        return (
          <MarketPointer
            key={index + '-market-in-map-viewport-pointer'}
            lat={market.latitude}
            lng={market.longitude}
            name={market.name}
            value={`${abbreviateNumber(market.supply)} listings`}
            url={market.url}
          />
        );
      });
  }, [marketsInMapViewport, matches]);

  const handleTailoredRegionMapChange = useCallback(({ center }) => {
    const centerLat = center.lat();
    const centerLng = center.lng();
    setCenterLat(centerLat);
    setCenterLng(centerLng);
  }, []);

  useEffect(() => {
    if (!params.has('lat') || !params.has('lng') || wrongLatLng) {
      navigator.geolocation.getCurrentPosition(
        position => {
          setHomeLat(position.coords.latitude);
          setHomeLng(position.coords.longitude);
          setCenterLat(position.coords.latitude);
          setCenterLng(position.coords.longitude);
          setLocationAllowed(true);
        },
        error => {
          console.error('Error Code = ' + error.code + ' - ' + error.message);
        }
      );
    }
  }, []);

  const setCustomMarket = async () => {
    // setIsTailoredRegionOpen(false);
    setLoadingCustomMarket(true);

    if (nameLoading || nameUpdateStatus.error) return;

    if (tailoredListings >= 10000) {
      const userEmail = localStorage.getItem('user-email');
      postOnSlack(
        'customer',
        `[MARKET HAS MORE THAN 10K LISTINGS]: user: ${userEmail} Market Name: ${tailorRegionName}`
      );
    }

    if (nameUpdateStatus.message == 'Name is available!') {
      await checkOrUpdateTailoredRegionName('rename');
      if (nameUpdateStatus.error) return;
    }

    if (tailoredRegionId) {
      dispatch(updateMarketInList(selectedList, tailoredRegionId, 'add', ''));
    }

    // Taking delay to trigger the addition of the market id into the list.
    setTimeout(
      () => {
        history.push(`/airbnb-data/0/0/${editableName}`);

        window.location.reload();
      },
      tailoredRegionId ? 1000 : 0
    );
  };

  const lastURL = useRef<string | null>(null);
  const urlRestored = useRef<boolean>(false);

  useEffect(() => {
    lastURL.current =
      window.location.pathname + window.location.search + window.location.hash;

    if (window.location.pathname !== '/tailored/t') {
      window.history.pushState({}, '', '/tailored/t');
    }

    return () => {
      // Restore if haven't already restored the URL
      if (!urlRestored.current && lastURL.current) {
        const pathToRestore = lastURL.current !== '/tailored/t'
          ? lastURL.current
          : '/';

        // Do replace to avoid adding to history stack
        history.replace(pathToRestore);
        urlRestored.current = true;
      }
    };
  }, [history]);



  const resetTailoredRegion = () => {
    // if (firstRender) return;
    initiateMapFromDrawing(polygonCopy, setPolygonCopy, setIsLoading);
    setDownBarSize('90px');
    setTailoredListings(0);
    setApiCallStatus('');
    setDisableButton(true);
    dispatch(setTailoredRegionCreationRateLimited(false));
  };

  const closeTailoredRegion = () => {
    // Restore URL before closing
    if (!urlRestored.current && lastURL.current) {
      const pathToRestore = lastURL.current !== '/tailored/t'
        ? lastURL.current
        : '/';
      history.replace(pathToRestore);
      urlRestored.current = true;
    }
    setIsTailoredRegionOpen(false);
  };

  const subHeading = alertText ? (
    <Typography sx={{ color: '#000' }}>{alertText}</Typography>
  ) : (
    <Typography sx={{ color: '#000' }}>
      Zoom in and move around the map to locate your neighborhood. Click the "Draw Your Market"
      button at the bottom center of the map to define your own market.
    </Typography>
  );

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Tailored Region - Airbtics</title>
      </Helmet>
      <Box sx={{ height: '90vh' }}>
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          style={{ height: '100%' }}
        >
          <div className={`${classes.paperModal} modal-custom`}>
            <div className={classes.sideButtonWrapper}>
              <IconButton className={classes.iconButton} onClick={closeTailoredRegion}>
                <CloseIcon
                  className={classes.tailoredRegionMapButton}
                  style={{ color: COLOR_RED }}
                />
              </IconButton>

              {matches && (
                <IconButton className={classes.iconButton} onClick={() => setIsInfoModalOpen(true)}>
                  <NotListedLocationIcon
                    className={classes.tailoredRegionMapButton}
                    style={{
                      color: '#000',
                    }}
                  />
                </IconButton>
              )}
            </div>

            {isLoading && (
              <LinearProgress
                sx={{
                  position: 'absolute',
                  bottom: 15,
                  left: '5%',
                  right: '5%',
                  zIndex: 2000,
                  width: '90%',
                  height: '5px',
                  '& .MuiLinearProgress-barColorPrimary': {
                    backgroundColor: COLOR_PRIMARY_DARK,
                  },
                }}
              />
            )}

            <Paper className={classes.tailoredRegionMapContainer}>
              {AlertTextComponent && AlertTextComponent}

              {!matches && (
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  style={{
                    padding: AlertTextComponent ? '5px 150px' : '40px 150px',
                    width: '100%',
                  }}
                  item
                >
                  <Typography
                    variant="h5"
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      marginBottom: AlertTextComponent ? 5 : 20,
                    }}
                  >
                    {title ? title : 'Select Location'}
                  </Typography>
                  {subHeading}
                </Grid>
              )}

              <GoogleMapReact
                bootstrapURLKeys={{
                  key: GOOGLE_MAPS_API_KEY,
                  libraries: ['drawing', 'places', 'visualization'],
                  id: 'onboarding-modal-map',
                  v: '3.64',
                } as any}
                center={center ? center : getDefaultCenterLocation()}
                options={{
                  ...mapOptions,
                  fullscreenControl: false,
                  zoomControlOptions: { position: maps ? maps.ControlPosition.RIGHT_CENTER : 4 },
                }}
                zoom={zoom ? zoom : 4}
                resetBoundsOnResize
                onChange={handleMapChange}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) => {
                  tailoredRegionApiIsLoaded(
                    map,
                    maps,
                    boundaryInfo ? boundaryInfo.polygon : search.data.polygon,
                    boundaryInfo ? boundaryInfo.multiPolygon : search.data.multiPolygon
                  );
                  callbackAfterMapIsLoaded(
                    map,
                    maps,
                    boundaryInfo ? boundaryInfo.polygon : search.data.polygon,
                    boundaryInfo ? boundaryInfo.multiPolygon : search.data.multiPolygon
                  );
                }}
                onDragEnd={map => handleTailoredRegionMapChange(map)}
              >
                {locationPin}
                {markers}
                {marketInMapViewportMarkers}
              </GoogleMapReact>

              {!isLoading && !isDrawing && (
                <Button
                  size="large"
                  variant="outlined"
                  onClick={() => {
                    if (polygonCopy) {
                      resetTailoredRegion();
                    } else {
                      togglePolygonDrawing(maps, drawingManagerCopy, isDrawing, setIsDrawing);
                    }
                  }}
                  style={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bottom: downBarSize,
                    marginBottom: '20px',
                    zIndex: 100,
                    background: COLOR_PRIMARY_2,
                    color: 'white',
                  }}
                >
                  {polygonCopy ? <RestartAltIcon /> : <DrawIcon />}
                  {polygonCopy
                    ? 'Reset'
                    : showMarketPolygon
                      ? 'Draw Your Submarket'
                      : 'Draw Your Market'}
                </Button>
              )}
            </Paper>

            <Grid
              container
              direction="column"
              justify="center"
              alignItems="flex-start"
              style={{ height: downBarSize }}
              className={classes.tailoredRegionMapBottomButtonContainer}
            >
              {!hideListingsCount && (
                <Grid
                  item
                  xs={12}
                  style={{
                    // paddingBottom: '10px',
                    flexBasis: 0,
                    fontWeight: 900,
                    width: '100%',
                    margin: 'auto',
                    marginBottom: 20,
                    marginTop: -5,
                    textAlign: 'center',
                    paddingLeft: '5%',
                    paddingRight: '5%',
                  }}
                >
                  {tailoredRegionCreationRateLimited && (
                        <RateLimitAlert />
                      )}

                  {!isLoading && apiCallStatus != 'success' ? (
                    <Grid container direction="column" justify="flex-start" alignItems={'center'}>                      

                      <Typography style={{ fontWeight: 'bold' }}>
                        {tailoredListings} listings are selected
                      </Typography>

                      {triggerRectangle && apiCallStatus === 'notAllowed' && (
                        <Collapse in={triggerRectangle && apiCallStatus === 'notAllowed'}>
                          {tailoredListings < 10 ? (
                            <Alert severity="error">
                              Please draw a larger square so that it includes more than 10 listings!
                            </Alert>
                          ) : (
                            <Alert severity="error">
                              Please draw a smaller square so that it includes less than 30000
                              listings!
                            </Alert>
                          )}
                        </Collapse>
                      )}

                      <Collapse in={apiCallStatus === 'failed'}>
                        <Alert severity="error">
                          Oops! Something went wrong. Please retry. If error persists, contact
                          support@airbtics.com, please.
                        </Alert>
                      </Collapse>
                    </Grid>
                  ) : (
                    <Grid>
                      <Typography style={{ marginBottom: 10, marginTop: -20, fontWeight: 'bold' }}>
                        Selected boundary has {tailoredListings} listings.
                      </Typography>

                      {!isLoading && tailorRegionName && (
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                          Custom Market Name
                        </Typography>
                      )}

                      {!isLoading && tailorRegionName && !tailoredRegionCreationRateLimited && (
                        <TextField
                          // autoFocus={true}
                          variant="standard"
                          fullWidth
                          value={editableName}
                          onChange={handleNameCheck}
                          error={nameUpdateStatus.error}
                          helperText={
                            nameLoading ? <CircularProgress size={20} /> : nameUpdateStatus.message
                          }
                          FormHelperTextProps={{
                            style: { color: nameUpdateStatus.error ? 'red' : 'green' },
                          }}
                        />
                      )}
                    </Grid>
                  )}
                </Grid>
              )}

              {showMarketPolygon && (
                <Grid
                  item
                  xs={12}
                  style={{
                    // paddingBottom: '10px',
                    flexBasis: 0,
                    fontWeight: 900,
                    width: '100%',
                    margin: 'auto',
                    marginBottom: 20,
                    marginTop: -5,
                    textAlign: 'center',
                    paddingLeft: '5%',
                    paddingRight: '5%',
                  }}
                >
                  <Collapse in={polygonOutOfBounds}>
                    <Alert severity="error">
                      You can only create this submarket inside {formatMarketName(search.data)}
                    </Alert>
                  </Collapse>
                </Grid>
              )}

              <Button
                size="small"
                variant="outlined"
                className={classes.button}
                onClick={
                  handleConfirmation ? (loading ? () => { } : handleConfirmation) : setCustomMarket
                }
                style={{ transform: disableButton || tailoredRegionCreationRateLimited ? 'scale(0)' : 'scale(1)' }}
                disabled={loadingCustomMarket}
              >
                {confirmationButtonText ? (
                  loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    confirmationButtonText
                  )
                ) : (
                  'Set your own market'
                )}
                {loading ||
                  (loadingCustomMarket && (
                    <CircularProgress size={20} style={{ marginLeft: 1, color: 'white' }} />
                  ))}
              </Button>
            </Grid>
          </div>
        </Grid>

        <Modal
          open={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(0, 0, 0, 0.1)',
            height: '100%',
            width: '100%',
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              width: '70%',
              bgcolor: COLOR_PRIMARY_LIGHT,
              boxShadow: 24,
              p: 4,
              borderRadius: '20px',
              animation: 'fade-scale-in 0.3s ease forwards',
            }}
          >
            {subHeading}
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                setIsInfoModalOpen(false);
              }}
              style={{
                background: '#fff',
                color: '#000',
                borderRadius: 100,
                padding: '5px auto',
                outline: 'none',
                border: 'none',
                textTransform: 'capitalize',
                marginTop: 16,
                width: '100%',
                fontSize: '18px',
                fontWeight: 'bold',
              }}
            >
              I Understood
            </Button>
          </Box>
        </Modal>

        {wrongLatLng && (
          <ErrorDialog
            open={wrongLatLng}
            setOpen={setWrongLatLng}
            title={'Error! Wrong Location Information!'}
            body={`Unfortunately we are unable to find this location. Please draw your location on the map here.`}
          />
        )}
      </Box>
    </>
  );
};

export default OnboardingModalMobile;
